import React, { useEffect, useState } from "react";
import Stack from "@mui/material/Stack";
import {
  alpha,
  Button,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import Div from "@jumbo/shared/Div";
import { useDispatch, useSelector } from "react-redux";
import Loader from "@jumbo/components/Loader";
import { getInventoryList } from "app/redux/slice/inventorySlice";

const InventoryManagement = () => {
  const dispatch = useDispatch();

  const fabricData = useSelector((state) => state?.inventory);
  const [searchText, setSearchText] = useState("");
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("productName");
  const [isInitialized, setIsInitialized] = useState(false);

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "fabric" });
  useEffect(() => {
    const savedSearchText = localStorage.getItem("inventory-search") || "";
    setSearchText(savedSearchText);
    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const columns = [
    { id: "productName", label: "Product Name", sort: true },
    { id: "totalIn", label: "Total In", sort: true },
    { id: "totalOut", label: "Total Out", sort: true },
    { id: "totalSell", label: "Total Sell", sort: true },
    { id: "costPrice", label: "Cost (Per Unit)", sort: true },
    { id: "total", label: "Current Stock", sort: true },
    { id: "currentValue", label: "Current Value", sort: true, isTotal: true },
    { id: "approxRevenue", label: "Approx Revenue", sort: true, isTotal: true },
  ];

  /**
   * A product that has been switched off still holds stock and still shows
   * here, but it is gone from the app - so its row is tinted red to say so
   * at a glance. The hover tint is set too, otherwise moving the mouse over
   * the row would paint the mark away.
   */
  const rowSx = (row) =>
    row?.isActive === false
      ? {
          backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
          "&:hover": {
            backgroundColor: (theme) =>
              `${alpha(theme.palette.error.main, 0.16)} !important`,
          },
        }
      : undefined;

  const getData = () => {
    localStorage.setItem("inventory-search", searchText.trim());
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getInventoryList(value));
  };

  useEffect(() => {
    if (isInitialized) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, dispatch, order, orderBy, isInitialized]);

  useEffect(() => {
    if (isInitialized && searchText !== "") {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  return (
    <>
      {fabricData.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12} sm={10}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={() => {
                              setSearchText("");
                              localStorage.removeItem("inventory-search");
                            }}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleChangePage(0, 0);
                        getData();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Inventory Management
                </Typography>
              </Stack>
              <Table
                columns={columns}
                rows={fabricData.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={fabricData.total}
                OrderBy={orderBy}
                sortData={sortData}
                rowSx={rowSx}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                showTotalInFooter
              />
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
};

export default InventoryManagement;
